import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link, useParams } from 'react-router-dom';
import { Search, Filter, X, Grid, List, Egg, Users, Calendar, DollarSign, ChevronRight, Clock, Baby, CheckCircle2, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { ListingCard, ListingCardSkeleton } from '../components/ListingCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

const CLUTCH_STATUS_MAP = {
  planned: { label: 'Planeada', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  paired: { label: 'Emparejada', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  ovulating: { label: 'Ovulando', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
  laid: { label: 'Puesta', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  incubating: { label: 'Incubando', color: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  hatching: { label: 'Por nacer', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  available: { label: 'Disponible', color: 'bg-emerald-600/10 text-emerald-300 border-emerald-500/20' },
  assigned: { label: 'Asignada', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  completed: { label: 'Completada', color: 'bg-zinc-600/10 text-zinc-500 border-zinc-500/20' },
};

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug: urlCategorySlug } = useParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [exploreTab, setExploreTab] = useState('listings');
  const [clutches, setClutches] = useState([]);
  const [clutchesLoading, setClutchesLoading] = useState(false);
  const [clutchesTotal, setClutchesTotal] = useState(0);
  
  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoryId, setCategoryId] = useState(searchParams.get('category') || 'all');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [morphFilter, setMorphFilter] = useState('');
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [sex, setSex] = useState(searchParams.get('sex') || 'all');
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (exploreTab === 'listings') loadListings();
  }, [page, categoryId, sex, minPrice, maxPrice, sortBy, speciesFilter, morphFilter, exploreTab]);

  useEffect(() => {
    if (exploreTab === 'clutches') {
      loadClutches();
    }
  }, [exploreTab, categoryId, categories]);

  useEffect(() => {
    if (categoryId && categoryId !== 'all') {
      const cat = categories.find(c => c.id === categoryId);
      if (cat?.slug) {
        api.getSpeciesBySlug(cat.slug).then(data => setSpeciesOptions(data.species || [])).catch(() => setSpeciesOptions([]));
      }
    } else {
      setSpeciesOptions([]);
    }
    setSpeciesFilter('all');
    setMorphFilter('');
  }, [categoryId, categories]);

  const loadCategories = async () => {
    try {
      const data = await api.getCategories();
      setCategories(data);
      // If URL has a category slug, pre-select it
      if (urlCategorySlug && data.length > 0) {
        const match = data.find(c => c.slug === urlCategorySlug);
        if (match) {
          setCategoryId(match.id);
        }
      }
    } catch (e) {
      console.error('Error loading categories:', e);
    }
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        search: search || undefined,
        category_id: categoryId && categoryId !== 'all' ? categoryId : undefined,
        sex: sex && sex !== 'all' ? sex : undefined,
        min_price: minPrice || undefined,
        max_price: maxPrice || undefined,
        species_canonical: speciesFilter && speciesFilter !== 'all' ? speciesFilter : undefined,
        morph_canonical: morphFilter || undefined,
      };
      const data = await api.getListings(params);
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Error loading listings:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadClutches = async () => {
    setClutchesLoading(true);
    try {
      const params = {};
      if (categoryId && categoryId !== 'all') {
        const cat = categories.find(c => c.id === categoryId);
        if (cat?.slug) params.category_slug = cat.slug;
      }
      const data = await api.getClutches(params);
      setClutches(data.clutches || []);
      setClutchesTotal(data.total || 0);
    } catch (e) { console.error('Clutches load error:', e); }
    finally { setClutchesLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    if (exploreTab === 'listings') loadListings();
    else loadClutches();
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryId('all');
    setSpeciesFilter('all');
    setMorphFilter('');
    setSex('all');
    setMinPrice('');
    setMaxPrice('');
    setPage(1);
  };

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <label className="text-sm font-medium text-zinc-300 mb-2 block">Categoria</label>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100">
            <SelectValue placeholder="Todas las categorias" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todas</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium text-zinc-300 mb-2 block">Especie</label>
        <Select value={speciesFilter} onValueChange={setSpeciesFilter} disabled={!speciesOptions.length}>
          <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100">
            <SelectValue placeholder={speciesOptions.length ? "Todas las especies" : "Selecciona categoria primero"} />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todas</SelectItem>
            {speciesOptions.filter(s => !s.is_other).map(sp => (
              <SelectItem key={sp.canonical_slug} value={sp.canonical_slug}>{sp.display_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {exploreTab === 'listings' && (
        <>
          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 block">Morph / Genetica</label>
            <Input
              type="text"
              placeholder="Ej: Pastel, Clown..."
              value={morphFilter}
              onChange={(e) => setMorphFilter(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              className="bg-white/5 border-white/10 text-zinc-100"
              data-testid="explore-morph-filter"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 block">Sexo</label>
            <Select value={sex} onValueChange={setSex}>
              <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="bg-[#121215] border-white/10">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="macho">Macho</SelectItem>
                <SelectItem value="hembra">Hembra</SelectItem>
                <SelectItem value="desconocido">Desconocido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 block">Precio (MXN)</label>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Min" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="bg-white/5 border-white/10 text-zinc-100" />
              <Input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="bg-white/5 border-white/10 text-zinc-100" />
            </div>
          </div>
        </>
      )}
      <Button variant="outline" className="w-full border-white/10 text-zinc-400" onClick={clearFilters}>
        Limpiar filtros
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24" data-testid="explore-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-zinc-100 mb-2 font-[Outfit]">
            Explorar
          </h1>
          <p className="text-zinc-500">
            {exploreTab === 'listings'
              ? `${total} ${total === 1 ? 'anuncio disponible' : 'anuncios disponibles'}`
              : `${clutchesTotal} ${clutchesTotal === 1 ? 'camada activa' : 'camadas activas'}`
            }
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/5 w-fit mb-6" data-testid="explore-tab-switcher">
          <button
            onClick={() => setExploreTab('listings')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              exploreTab === 'listings'
                ? 'bg-white/10 text-zinc-100 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="tab-listings"
          >
            Anuncios
          </button>
          <button
            onClick={() => setExploreTab('clutches')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              exploreTab === 'clutches'
                ? 'bg-emerald-500/15 text-emerald-400 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid="tab-clutches"
          >
            <Egg className="w-4 h-4" /> Camadas
          </button>
        </div>

        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <Input
                type="text"
                placeholder={exploreTab === 'listings' ? "Buscar por nombre, morph, especie..." : "Buscar camadas..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 bg-white/5 border-white/10 text-zinc-100 placeholder-zinc-500 h-12"
                data-testid="explore-search"
              />
            </div>
          </form>
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden border-white/10 text-zinc-300">
                  <Filter className="w-4 h-4 mr-2" /> Filtros
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="bg-[#121215] border-white/10">
                <SheetHeader>
                  <SheetTitle className="text-zinc-100">Filtros</SheetTitle>
                </SheetHeader>
                <div className="py-6"><FilterContent /></div>
              </SheetContent>
            </Sheet>
            {exploreTab === 'listings' && (
              <>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-zinc-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121215] border-white/10">
                    <SelectItem value="recent">Mas recientes</SelectItem>
                    <SelectItem value="price_asc">Menor precio</SelectItem>
                    <SelectItem value="price_desc">Mayor precio</SelectItem>
                    <SelectItem value="popular">Mas populares</SelectItem>
                  </SelectContent>
                </Select>
                <div className="hidden md:flex items-center rounded-lg border border-white/10 p-1">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white/10' : ''}`}>
                    <Grid className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-white/10' : ''}`}>
                    <List className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="sticky top-24 glass-card p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-6">Filtros</h3>
              <FilterContent />
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1">
            {/* Active Filters */}
            {(categoryId !== 'all' || sex !== 'all' || minPrice || maxPrice) && (
              <div className="flex flex-wrap gap-2 mb-6">
                {categoryId !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm">
                    {categories.find(c => c.id === categoryId)?.name}
                    <button onClick={() => setCategoryId('all')}><X className="w-3 h-3" /></button>
                  </span>
                )}
                {sex !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
                    {sex}
                    <button onClick={() => setSex('all')}><X className="w-3 h-3" /></button>
                  </span>
                )}
              </div>
            )}

            {/* LISTINGS TAB */}
            {exploreTab === 'listings' && (
              <>
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => <ListingCardSkeleton key={i} />)}
                  </div>
                ) : listings.length > 0 ? (
                  <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                    {listings.map(listing => (
                      <ListingCard key={listing.id} listing={listing} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 glass-card">
                    <p className="text-zinc-500 mb-4">No se encontraron anuncios</p>
                    <Button variant="outline" onClick={clearFilters} className="border-white/10 text-zinc-400">Limpiar filtros</Button>
                  </div>
                )}
                {total > 20 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="border-white/10 text-zinc-400">Anterior</Button>
                    <span className="flex items-center px-4 text-zinc-500">Pagina {page}</span>
                    <Button variant="outline" disabled={listings.length < 20} onClick={() => setPage(p => p + 1)} className="border-white/10 text-zinc-400">Siguiente</Button>
                  </div>
                )}
              </>
            )}

            {/* CLUTCHES TAB */}
            {exploreTab === 'clutches' && (
              <>
                {/* CTA Banner */}
                <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-500/5 to-teal-500/5 border border-emerald-500/10">
                  <p className="text-emerald-400 text-sm font-medium">Proximas crias disponibles para reserva</p>
                  <p className="text-zinc-500 text-xs mt-1">Unete a la waitlist de una camada y asegura tu lugar antes del hatch</p>
                </div>

                {clutchesLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="glass-card p-5 animate-pulse">
                        <div className="h-4 bg-zinc-800 rounded w-2/3 mb-3" />
                        <div className="h-3 bg-zinc-800 rounded w-1/2 mb-2" />
                        <div className="h-3 bg-zinc-800 rounded w-1/3" />
                      </div>
                    ))}
                  </div>
                ) : clutches.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {clutches.map(clutch => {
                      const st = CLUTCH_STATUS_MAP[clutch.status] || CLUTCH_STATUS_MAP.planned;
                      const requiresDeposit = clutch.waitlist_mode === 'deposit' || clutch.waitlist_mode === 'both';
                      return (
                        <div key={clutch.id} className="glass-card overflow-hidden group hover:border-emerald-500/20 transition-all" data-testid={`clutch-explore-card-${clutch.id}`}>
                          {/* Image or placeholder */}
                          <div className="relative h-40 bg-zinc-900">
                            {clutch.parent_images?.[0] ? (
                              <img src={clutch.parent_images[0]} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Egg className="w-12 h-12 text-zinc-700" />
                              </div>
                            )}
                            {/* Status badge */}
                            <span className={`absolute top-3 left-3 text-[10px] px-2.5 py-1 rounded-full border backdrop-blur-sm ${st.color}`}>
                              {st.label}
                            </span>
                            {requiresDeposit && (
                              <span className="absolute top-3 right-3 text-[10px] px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/20 backdrop-blur-sm">
                                <DollarSign className="w-3 h-3 inline" /> Deposito
                              </span>
                            )}
                          </div>

                          <div className="p-5">
                            {/* Species & Morph */}
                            <h3 className="text-zinc-100 font-medium text-base mb-1 truncate">
                              {clutch.species_name}
                            </h3>
                            {clutch.morph_expected && (
                              <p className="text-emerald-400/80 text-sm mb-2 truncate">{clutch.morph_expected}</p>
                            )}

                            {/* Breeder */}
                            <div className="flex items-center gap-2 mb-3">
                              {clutch.breeder_avatar ? (
                                <img src={clutch.breeder_avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400 text-[10px] font-medium">
                                  {(clutch.breeder_name || '?')[0].toUpperCase()}
                                </div>
                              )}
                              <span className="text-zinc-400 text-xs truncate">{clutch.breeder_name}</span>
                            </div>

                            {/* Meta info */}
                            <div className="space-y-1.5 text-xs text-zinc-500 mb-4">
                              {clutch.estimated_hatch_date && (
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5" />
                                  <span>Hatch: {clutch.estimated_hatch_date}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5" />
                                <span>{clutch.waitlist_count || 0} en waitlist</span>
                              </div>
                              {(clutch.price_range_min > 0 || clutch.price_range_max > 0) && (
                                <div className="flex items-center gap-1.5">
                                  <DollarSign className="w-3.5 h-3.5" />
                                  <span>${clutch.price_range_min?.toLocaleString('es-MX')} - ${clutch.price_range_max?.toLocaleString('es-MX')} MXN</span>
                                </div>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                              <Link to={`/camadas/${clutch.id}`} className="flex-1">
                                <Button variant="outline" className="w-full border-white/10 text-zinc-300 hover:text-zinc-100 text-xs h-9" data-testid={`view-clutch-${clutch.id}`}>
                                  Ver camada <ChevronRight className="w-3.5 h-3.5 ml-1" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-20 glass-card">
                    <Egg className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-400 mb-2">No hay camadas activas</p>
                    <p className="text-zinc-600 text-sm">Vuelve pronto para ver nuevas camadas disponibles</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
